/**
 * The Environment editor's one draft, seen two ways: rows of key and value, and the
 * text of a `.env` file. Switching views converts between them here, and the
 * conversion is lossless for what the user did not touch — a row read from the file
 * and left alone writes back its original lines (quoting, `export`, an inline
 * comment), and comments and stray lines ride along as rows the key/value view does
 * not show. Errors name a row or a line, never a value.
 */

import { ENV_KEY_NAME } from "./env-preview";

export interface EnvRow {
  id: string;
  /** `pair` is KEY=value; `other` is a comment, a blank or a line that is not KEY=value. */
  kind: "pair" | "other";
  key: string;
  value: string;
  /** The lines this row was read from, written back while it is unchanged. */
  raw: string[] | null;
  /** The key and value as read, to tell whether the row was edited. */
  base: { key: string; value: string } | null;
}

const KEY_LINE = /^\s*(?:export\s+)?([A-Za-z_][A-Za-z0-9_.-]*)\s*=(.*)$/;

let counter = 0;
export const newRowId = (): string => `r${++counter}`;

export const emptyRow = (): EnvRow => ({ id: newRowId(), kind: "pair", key: "", value: "", raw: null, base: null });

function closingIndex(text: string, quote: string, from: number): number {
  for (let i = from; i < text.length; i++) {
    if (quote === '"' && text[i] === "\\") {
      i++;
      continue;
    }
    if (text[i] === quote) return i;
  }
  return -1;
}

const UNESCAPE: Record<string, string> = { n: "\n", r: "\r", t: "\t", '"': '"', "\\": "\\", $: "$" };

/** The value a dotenv reader sees for the text after `=` (continuation lines joined with `\n`). */
export function decodeEnvValue(rawValue: string): string {
  const text = rawValue.trimStart();
  const quote = text[0];
  if (quote === '"' || quote === "'" || quote === "`") {
    const end = closingIndex(text, quote, 1);
    const inner = end === -1 ? text.slice(1) : text.slice(1, end);
    if (quote !== '"') return inner;
    return inner.replace(/\\(.)/gs, (match, char: string) => UNESCAPE[char] ?? match);
  }
  // Unquoted: an inline comment starts at whitespace then #.
  const comment = text.search(/\s#/);
  return (comment === -1 ? text : text.slice(0, comment)).trim();
}

const BARE = /^[A-Za-z0-9_./:@%+,=~^-]+$/;

/** The text to write after `=` so that `decodeEnvValue` reads `value` back. */
export function encodeEnvValue(value: string): string {
  if (value === "") return "";
  if (BARE.test(value)) return value;
  if (!value.includes("'") && !/[\r\n]/.test(value)) return `'${value}'`;
  return `"${value.replace(/\\/g, "\\\\").replace(/"/g, '\\"').replace(/\$/g, "\\$").replace(/\r/g, "\\r").replace(/\n/g, "\\n")}"`;
}

export function rowsFromEnv(text: string): EnvRow[] {
  const lines = text.split(/\r?\n/);
  if (lines.length > 0 && lines[lines.length - 1] === "") lines.pop();
  const rows: EnvRow[] = [];
  for (let i = 0; i < lines.length; i++) {
    const line = lines[i]!;
    const match = KEY_LINE.exec(line);
    if (!match) {
      rows.push({ id: newRowId(), kind: "other", key: "", value: "", raw: [line], base: null });
      continue;
    }
    const raw = [line];
    const after = match[2]!.trimStart();
    const quote = after[0];
    if ((quote === '"' || quote === "'") && closingIndex(after, quote, 1) === -1) {
      // A quoted value continues to the line that closes it.
      while (i + 1 < lines.length) {
        const next = lines[++i]!;
        raw.push(next);
        if (closingIndex(next, quote, 0) !== -1) break;
      }
    }
    const value = decodeEnvValue([match[2]!, ...raw.slice(1)].join("\n"));
    rows.push({ id: newRowId(), kind: "pair", key: match[1]!, value, raw, base: { key: match[1]!, value } });
  }
  return rows;
}

const isBlankPair = (row: EnvRow) => row.kind === "pair" && row.key.trim() === "" && row.value === "";

export function envFromRows(rows: EnvRow[]): string {
  const out: string[] = [];
  for (const row of rows) {
    if (row.kind === "other") {
      out.push(...(row.raw ?? []));
      continue;
    }
    if (isBlankPair(row)) continue;
    const key = row.key.trim();
    if (row.raw && row.base && row.base.key === key && row.base.value === row.value) out.push(...row.raw);
    else out.push(`${key}=${encodeEnvValue(row.value)}`);
  }
  return out.length === 0 ? "" : `${out.join("\n")}\n`;
}

/** What stops a save, by row number among the key/value rows (1-based, as shown). */
export function rowProblems(rows: EnvRow[]): string[] {
  const problems: string[] = [];
  const seen = new Map<string, number>();
  let n = 0;
  for (const row of rows) {
    if (row.kind !== "pair") continue;
    n++;
    if (isBlankPair(row)) continue;
    const key = row.key.trim();
    if (!key) problems.push(`Row ${n} has a value but no key.`);
    else if (!ENV_KEY_NAME.test(key)) problems.push(`Row ${n}: "${key}" is not a key name (letters, digits, _ . -; not starting with a digit).`);
    else if (seen.has(key)) problems.push(`${key} is on rows ${seen.get(key)} and ${n}; the second would win.`);
    else seen.set(key, n);
  }
  return problems;
}

export const pairRows = (rows: EnvRow[]): EnvRow[] => rows.filter((row) => row.kind === "pair");

/** Comments and lines that are not KEY=value, kept as written but not shown as rows. */
export const hiddenLineCount = (rows: EnvRow[]): number =>
  rows.filter((row) => row.kind === "other" && (row.raw ?? []).some((line) => line.trim() !== "")).length;
